/**
 * config-data.ts 的公共工具函数
 */

/**
 * 从 configDataWhole 中安全获取数字值
 */
export const getNumberValue = (
  configDataWhole: WatchRendererOptions['configData'],
  key: string,
  defaultValue: number = 0
): number => {
  return Number(configDataWhole?.[key] ?? defaultValue);
};

/**
 * 格式化时间为 HH:MM 格式
 */
export const formatTime = (hour: number, minute: number): string => {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

/**
 * 格式化时间为 HH:MM:SS 格式（精确到秒）
 */
export const formatTimeWithSeconds = (
  hour: number,
  minute: number,
  second: number
): string => {
  return `${String(Math.floor(hour)).padStart(2, '0')}:${String(Math.floor(minute)).padStart(2, '0')}:${String(Math.floor(second)).padStart(2, '0')}`;
};

/**
 * 从 configDataWhole 中获取日出日落时间并格式化为 HH:MM
 */
export const getSunriseSunsetTime = (
  configDataWhole: WatchRendererOptions['configData']
): { sunriseTime: string; sunsetTime: string } | null => {
  if (!configDataWhole) return null;

  const sunRiseHour = getNumberValue(
    configDataWhole,
    'weatherCurrentSunRiseHour'
  );
  const sunRiseMinute = getNumberValue(
    configDataWhole,
    'weatherCurrentSunRiseMinute'
  );
  const sunSetHour = getNumberValue(
    configDataWhole,
    'weatherCurrentSunSetHour'
  );
  const sunSetMinute = getNumberValue(
    configDataWhole,
    'weatherCurrentSunSetMinute'
  );

  return {
    sunriseTime: formatTime(sunRiseHour, sunRiseMinute),
    sunsetTime: formatTime(sunSetHour, sunSetMinute)
  };
};

/**
 * 从 configDataWhole 中获取当前时间并格式化为 HH:MM:SS（精确到秒）
 */
export const getCurrentTime = (
  configDataWhole: WatchRendererOptions['configData']
): string | null => {
  if (!configDataWhole) return null;

  const timeHour = getNumberValue(configDataWhole, 'timeHour24H');
  const timeMinute = getNumberValue(configDataWhole, 'timeMinute');
  const timeSecond = getNumberValue(configDataWhole, 'timeSecond', 0);

  return formatTimeWithSeconds(timeHour, timeMinute, timeSecond);
};

/**
 * 将数字拆分为指定位数的数组（从右到左）
 * @param value 要拆分的数字
 * @param digits 位数
 * @returns 数字数组，索引0是最低位
 */
export const splitNumberToDigits = (
  value: number,
  digits: number
): number[] => {
  const str = Math.floor(value).toString().padStart(digits, '0');
  return str.split('').map(Number).reverse();
};

/**
 * 将数字拆分为高位和低位（2位数）
 */
export const splitNumberToHighLow = (
  value: number
): { high: number; low: number } => {
  const str = Math.floor(value).toString().padStart(2, '0');
  return {
    high: Number(str[0]),
    low: Number(str[1])
  };
};

/**
 * 将数字拆分为多个位数并设置到 configDataWhole
 * @param configDataWhole 配置数据对象
 * @param key 基础键名
 * @param value 要拆分的数字值
 * @param digits 位数
 * @param suffix 后缀（如 'Digit'）
 */
export const setNumberDigits = (
  configDataWhole: Record<string, string | number>,
  key: string,
  value: number,
  digits: number,
  suffix: string = 'Digit'
): void => {
  const digitArray = splitNumberToDigits(value, digits);
  digitArray.forEach((digit, index) => {
    configDataWhole[`${key}${suffix}${index + 1}`] = digit;
  });
};

/**
 * 将数字拆分为高位和低位并设置到 configDataWhole
 */
export const setNumberHighLow = (
  configDataWhole: Record<string, string | number>,
  key: string,
  value: number
): void => {
  const { high, low } = splitNumberToHighLow(value);
  configDataWhole[`${key}High`] = high;
  configDataWhole[`${key}Low`] = low;
};
